import * as React from 'react';
import { Home, User, PawPrint, Coffee, MoreHorizontal } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import BottomMoreMenu from './BottomMoreMenu';
import useSuggestDialog from '@/hooks/useSuggestDialog';
import { useUser } from '../contexts/useUser';
import { toast } from 'react-toastify';

type Props = { 
  className?: string; 
  onInfo?: () => void;
  onBeanLog?: () => void;
};

const BottomNav: React.FC<Props> = ({ className, onInfo, onBeanLog }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openSuggestDialog } = useSuggestDialog();
  const { logout } = useUser();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  // Determine active tab based on current route
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/' || path === '/search') return 'home';
    if (path === '/profile') return 'profile';
    if (path === '/hounds') return 'hounds';
    return 'home';
  };

  const [value, setValue] = React.useState(getActiveTab());

  React.useEffect(() => {
    setValue(getActiveTab());
  }, [location.pathname]);

  const handleLogout = React.useCallback(async () => {
    console.log('[BottomNav] Logout clicked');
    toast.info('Logging out...');
    setAnchorEl(null);
    try {
      console.log('[BottomNav] Calling logout...');
      await logout();
      console.log('[BottomNav] Logout successful, waiting before redirect');
      
      // Verify flag is set
      console.log('[BottomNav] Logout flag in localStorage:', localStorage.getItem('isLoggingOut'));
      
      // Wait longer to ensure everything is cleared
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Double-check flag is still set before redirect
      localStorage.setItem('isLoggingOut', 'true');
      console.log('[BottomNav] Re-set logout flag before redirect');
      
      toast.success('Logged out successfully');
      
      // Redirect to login page with full page reload
      window.location.href = '/login';
    } catch (error) {
      console.error('[BottomNav] Logout failed:', error);
      toast.error('Logout failed');
    }
  }, [logout]);

  return (
    <nav
      className={`${className} fixed left-0 right-0 z-[1300] sm:hidden bg-card-bg border-t border-card-border shadow-lg`}
      style={{
        bottom: '0px',
        paddingBottom: 'env(safe-area-inset-bottom)',
        // Forces its own compositing layer instead of being repositioned
        // during scroll — a long-standing iOS Safari fix for
        // position:fixed elements visually detaching from the viewport
        // edge mid-scroll (Android/Chrome never had this problem).
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
      }}
      data-testid="bottom-nav"
    >
      <div
        className="flex items-center justify-around"
        style={{ height: '56px' }}
        aria-label="mobile navigation"
      >
        <button
          onClick={() => navigate('/')}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '1 1 0',
            minWidth: '80px',
            maxWidth: '168px',
            height: '100%',
            padding: '6px 12px 8px',
            color: value === 'home' ? '#C68642' : 'var(--color-text-secondary)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
          data-testid="bottom-nav-home"
        >
          <Home style={{ width: '24px', height: '24px' }} />
          <span style={{ fontSize: '0.75rem', lineHeight: 1.2, marginTop: '2px' }}>Home</span>
        </button>
        <button
          onClick={() => navigate('/profile')}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '1 1 0',
            minWidth: '80px',
            maxWidth: '168px',
            height: '100%',
            padding: '6px 12px 8px',
            color: value === 'profile' ? '#C68642' : 'var(--color-text-secondary)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
          data-testid="bottom-nav-profile"
        >
          <User style={{ width: '24px', height: '24px' }} />
          <span style={{ fontSize: '0.75rem', lineHeight: 1.2, marginTop: '2px' }}>Profile</span>
        </button>
        <button
          onClick={() => onBeanLog?.()}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '1 1 0',
            minWidth: '80px',
            maxWidth: '168px',
            height: '100%',
            padding: '6px 12px 8px',
            color: value === 'beans' ? '#C68642' : 'var(--color-text-secondary)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
          data-testid="bottom-nav-beans"
        >
          <Coffee style={{ width: '24px', height: '24px' }} />
          <span style={{ fontSize: '0.75rem', lineHeight: 1.2, marginTop: '2px' }}>Beans</span>
        </button>
        <button
          onClick={() => navigate('/hounds')}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '1 1 0',
            minWidth: '80px',
            maxWidth: '168px',
            height: '100%',
            padding: '6px 12px 8px',
            color: value === 'hounds' ? '#C68642' : 'var(--color-text-secondary)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
          data-testid="bottom-nav-hounds"
        >
          <PawPrint style={{ width: '24px', height: '24px' }} />
          <span style={{ fontSize: '0.75rem', lineHeight: 1.2, marginTop: '2px' }}>Hounds</span>
        </button>
        <button
          onClick={(e) => setAnchorEl(e.currentTarget as HTMLElement)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: '1 1 0',
            minWidth: '80px',
            maxWidth: '168px',
            height: '100%',
            padding: '6px 12px 8px',
            color: value === 'more' ? '#C68642' : 'var(--color-text-secondary)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
          data-testid="bottom-nav-more"
        >
          <MoreHorizontal style={{ width: '24px', height: '24px' }} />
          <span style={{ fontSize: '0.75rem', lineHeight: 1.2, marginTop: '2px' }}>More</span>
        </button>
      </div>

      <BottomMoreMenu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onRecipes={() => {
          setAnchorEl(null);
          navigate('/recipes');
        }}
        onSuggest={() => {
          setAnchorEl(null);
          openSuggestDialog();
        }}
        onSettings={() => {
          setAnchorEl(null);
          navigate('/settings');
        }}
        onLogout={handleLogout}
      />
    </nav>
  );
};

export default BottomNav;
